/**
 * Renders a numeric spin button component
 *
 * @property {string} className
 * @property {integer} defaultValue
 * @property {boolean} disabled
 * @property {string} helper A hint or description
 * @property {string} label
 * @property {integer} max The maximum possible value
 * @property {integer} min The minimum possible value
 * @property {function} onChange
 * @property {boolean} required
 */

import React from 'react';

// Icons
import * as icons from '../Icons/Icons';

const SpinButton = class SpinButton extends React.Component {
    constructor(props) {
        super(props);

        // Set the initial state
        this.state = {
            value: props.defaultValue || 0
        }

        this.textInput = null;
        this.setTextInputRef = element => {
            this.textInput = element;
        }
        /*
        this.focusTextInput = () => {
            // Focus the text input using the raw DOM API
            if (this.textInput) this.textInput.focus();
        };

         */
        // Create refs
        //this.input = React.createRef();
    }

    componentDidUpdate(prevProps) {
        // If the defaultValue prop is updated, update the value in state
        if(prevProps.defaultValue !== this.props.defaultValue) {
            this.setState({
                value: this.props.defaultValue
            });
        }
    }

    /**
     * Increment and decrement with up and down keys.
     *
     * @function handleKeyDown
     * @param {event} e
     */
    handleKeyDown = e => {
        const code = e.code.toLowerCase();

        if(code === 'arrowup') {
            this.handleIncrement(1);
        } else if(code === 'arrowdown') {
            this.handleIncrement(-1);
        }
    }

    /**
     * Handle increment/decrement button click.
     *
     * @function handleIncrement
     * @param {integer} amount
     */
    handleIncrement = amount => {
        const { max, min } = this.props;
        let value = this.state.value + amount;

        if(value < min) {
            value = min;
        } else if(value > max) {
            value = max;
        }

        this.handleValidity(false);

        this.setState({
            value
        }, () => this.handleChange());
    }

    /**
     * Handle typing in the input
     *
     * @function handleInput
     * @param {string} value
     */
    handleInput = value => {
        const { max, min } = this.props;

        if(Number.isNaN(+value)) {
            return;
        }

        if(+value < min || +value > max) {
            this.handleValidity(true);
        } else {
            this.handleValidity(false);
        }

        this.setState({
            value: +value
        }, () => this.handleChange());
    }

    /**
     * Set input validity.
     *
     * @function handleValidity
     * @param {boolean} invalid
     */
    handleValidity = invalid => {
        const inputEl = this.textInput;

        if(invalid) {
            inputEl.setAttribute('aria-invalid', true);
            inputEl.setCustomValidity('Invalid value');
            inputEl.reportValidity();
        } else {
            inputEl.removeAttribute('aria-invalid');
            inputEl.setCustomValidity('');
            inputEl.reportValidity();
        }
    }

    /**
     * Handle onChange property
     *
     * @function handleChange
     */
    handleChange = () => {
        const { onChange } = this.props;
        const { value } = this.state;

        if(onChange) {
            onChange(value);
        }
    }

    render() {
        const {
            className,
            disabled,
            errorMsg,
            helper,
            id,
            label,
            layout,
            max,
            min,
            required
        } = this.props;
        const { value } = this.state;

        return (
            <div className={`spin-button${className ? ` ${className}` : ''}${layout ? ` spin-button--${layout}` : ''}`}>
                <div className="spin-button__head">
                    <label
                        className="spin-button__lbl hybrid-form__lbl"
                        htmlFor={id}
                    >
                        {label}
                    </label>

                    {helper &&
                    <span
                        className="spin-button__hint hybrid-form__hint"
                        id={`${id}-helper`}
                    >
              {helper}
            </span>
                    }
                </div>

                <div className="spin-button__wrap">
                    <input
                        aria-describedby={`${id}-helper`}
                        aria-valuemax={max}
                        aria-valuemin={min}
                        aria-valuenow={value}
                        className={`spin-button__input${
                            max <= 99 ? ' input--xs' : ''
                        }${
                            max > 99 & max <= 999 ? ' input--sm' : ''
                        }${
                            max > 999 & max <= 9999 ? ' input--md' : ''
                        }`}
                        data-errormsg={errorMsg || null}
                        disabled={disabled}
                        id={id}
                        inputMode="numeric"
                        onKeyDown={e => this.handleKeyDown(e)}
                        onChange={e => this.handleInput(e.target.value)}
                        pattern="[-+,0-9]*"
                        ref={this.setTextInputRef}
                        required={required}
                        role="spinbutton"
                        type="text"
                        value={value}
                    />

                    <button
                        aria-label={`decrease ${label}`}
                        aria-live="polite"
                        className="spin-button__control spin-button__down btn--reset btn--ghost"
                        disabled={value <= min || disabled}
                        onClick={() => this.handleIncrement(-1)}
                        tabIndex="-1"
                        type="button"
                    >
                        <icons.iconMinusHybrid />
                    </button>

                    <button
                        aria-label={`increase ${label}`}
                        aria-live="polite"
                        className="spin-button__control spin-button__up btn--reset btn--ghost"
                        disabled={value >= max || disabled}
                        onClick={() => this.handleIncrement(1)}
                        tabIndex="-1"
                        type="button"
                    >
                        <icons.iconPlusHybrid />
                    </button>
                </div>
            </div>
        )
    }
}

export default SpinButton;



// WEBPACK FOOTER //
// ./src/app/components/Common/SpinButton.js